import { useCallback, useState, useEffect, useRef } from "react";
import { useOrg } from "@/hooks/org/useOrg";
import { useMetadataProgressStore } from "@/stores/metadataProgressStore";
import { useMetadataTypes } from "@/hooks/metadata_v1";
import { fetchRetrievalMetadataItems } from "@/hooks/metadata_v1/useRetrievalMetadataItems";
import type { MetadataItem } from "@/interfaces/metadata/metadata";

type SelectedMetadata = { id: string; name: string } | null;

export function useSidebarMetadata(selectedMetadata: SelectedMetadata) {
  const { selectedOrg } = useOrg();
  const { selectedRetrievalId, setSelectedRetrievalId } = useMetadataProgressStore();
  const effectiveRetrievalId = selectedOrg?.latestMetadataRetrieval?.id;

  const [metadataFiles, setMetadataFiles] = useState<MetadataItem[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingMoreFiles, setLoadingMoreFiles] = useState(false);
  const [metadataSearchTerm, setMetadataSearchTerm] = useState("");
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const latestId = selectedOrg?.latestMetadataRetrieval?.id;
    if (latestId && latestId !== selectedRetrievalId) {
      setSelectedRetrievalId(latestId);
    }
  }, [selectedOrg?.latestMetadataRetrieval?.id, selectedRetrievalId, setSelectedRetrievalId]);

  const { data: metadataTypes } = useMetadataTypes({
    enabled: !!selectedOrg?.id && !!effectiveRetrievalId,
    orgId: selectedOrg?.id,
    retrievalId: effectiveRetrievalId,
  });

  const metadataTypeOptions =
    metadataTypes?.map((type) => ({
      id: type.metadataType,
      name: type.metadataType,
      value: type.metadataType,
      count: type.count,
    })) || [];

  const mapRetrievalItemsToMetadataFiles = useCallback(
    (data: Awaited<ReturnType<typeof fetchRetrievalMetadataItems>>) =>
      data.edges.flatMap((edge) =>
        edge.node.items.map((item) => ({
          id: item.id,
          name: item.fileName ?? item.fullName,
          fullName: item.fullName,
          type: item.metadataType,
          metadataType: item.metadataType,
          status: "active" as const,
          content: item.content || "",
          lastModified: item.lastModifiedDate || "",
        }))
      ),
    []
  );

  useEffect(() => {
    if (!selectedMetadata || !effectiveRetrievalId) {
      setMetadataFiles([]);
      setMetadataSearchTerm("");
      setEndCursor(null);
      setHasNextPage(false);
      return;
    }
    setLoadingFiles(true);
    setMetadataSearchTerm("");
    setMetadataFiles([]);
    setEndCursor(null);
    setHasNextPage(false);
    fetchRetrievalMetadataItems({
      retrievalId: effectiveRetrievalId,
      metadataType: selectedMetadata.id,
      first: 250,
    })
      .then((data) => {
        if (!isMountedRef.current) return;
        setMetadataFiles(mapRetrievalItemsToMetadataFiles(data));
        setEndCursor(data.pageInfo.endCursor ?? null);
        setHasNextPage(data.pageInfo.hasNextPage ?? false);
      })
      .catch((err) => {
        console.error("[useSidebarMetadata] fetchRetrievalMetadataItems failed:", err);
      })
      .finally(() => {
        if (isMountedRef.current) setLoadingFiles(false);
      });
  }, [selectedMetadata, effectiveRetrievalId, mapRetrievalItemsToMetadataFiles]);

  const handleLoadMore = useCallback(() => {
    if (!selectedMetadata || !effectiveRetrievalId || !endCursor || loadingMoreFiles) return;
    setLoadingMoreFiles(true);
    fetchRetrievalMetadataItems({
      retrievalId: effectiveRetrievalId,
      metadataType: selectedMetadata.id,
      first: 250,
      after: endCursor,
    })
      .then((data) => {
        if (!isMountedRef.current) return;
        setMetadataFiles((prev) => [
          ...prev,
          ...mapRetrievalItemsToMetadataFiles(data),
        ]);
        setEndCursor(data.pageInfo.endCursor ?? null);
        setHasNextPage(data.pageInfo.hasNextPage ?? false);
      })
      .catch((err) => {
        console.error("[useSidebarMetadata] handleLoadMore failed:", err);
      })
      .finally(() => {
        if (isMountedRef.current) setLoadingMoreFiles(false);
      });
  }, [
    selectedMetadata,
    effectiveRetrievalId,
    endCursor,
    loadingMoreFiles,
    mapRetrievalItemsToMetadataFiles,
  ]);

  return {
    selectedOrg,
    effectiveRetrievalId,
    metadataTypeOptions,
    metadataFiles,
    loadingFiles,
    loadingMoreFiles,
    metadataSearchTerm,
    setMetadataSearchTerm,
    hasNextPage,
    handleLoadMore,
  };
}
